/**
 * Binary codec for the render-app geometry cache: `WorkerOutput` ⇄ ArrayBuffer.
 *
 * Layout: [uint32 headerLen][JSON header][concatenated typed-array bytes]. The
 * header records, in a fixed traversal order, each serialized geo's per-attribute
 * element counts (position / normal / uv / index). Buffers are concatenated in the
 * same order; on decode they are COPIED into fresh typed arrays (the byte offsets
 * are not guaranteed to be 4-aligned, so views can't be created in place).
 *
 * Pure functions — no `three` runtime dependency (only type-only imports, erased).
 */
import type {WorkerOutput} from '../deviceMesh/deviceGeometryWorker'
import type {SerializedGeo} from './helpers'
import {GEOMETRY_CACHE_VERSION} from './geometryCacheVersion'

// Fixed field order — MUST stay in sync with the WorkerOutput interface.
const SINGLE_FIELDS = ['body', 'frontFace', 'display'] as const
const ARRAY_FIELDS = [
  'antennas',
  'sideButtons',
  'sideButtonSlots',
  'muteSwitchInserts',
  'muteSwitchSlots',
  'portShells',
  'portTongues',
  'portInserts',
  'frontSpeakerInserts',
  'frontSpeakerDiscs',
  'homeButtonPlugs',
  'speakerGrillDiscs',
  'speakerGrillSlots',
  'screwDiscs',
  'simCardSlotTrays',
  'simCardSlotSlots',
] as const

// Per-geo element counts; null length means the attribute is absent.
interface GeoHeader {
  p: number
  n: number
  u: number | null
  i: number | null
}

interface CacheHeader {
  v: number
  single: Record<string, GeoHeader>
  array: Record<string, GeoHeader[]>
}

const geoHeader = (g: SerializedGeo): GeoHeader => ({
  p: g.position.length,
  n: g.normal.length,
  u: g.uv ? g.uv.length : null,
  i: g.index ? g.index.length : null,
})

const pushGeoBuffers = (g: SerializedGeo, out: ArrayBufferView[]): void => {
  out.push(g.position, g.normal)
  if (g.uv) out.push(g.uv)
  if (g.index) out.push(g.index)
}

export const encodeWorkerOutput = (output: WorkerOutput): ArrayBuffer => {
  const header: CacheHeader = {v: GEOMETRY_CACHE_VERSION, single: {}, array: {}}
  const buffers: ArrayBufferView[] = []

  for (const key of SINGLE_FIELDS) {
    const geo = output[key]
    header.single[key] = geoHeader(geo)
    pushGeoBuffers(geo, buffers)
  }
  for (const key of ARRAY_FIELDS) {
    const geos = output[key]
    header.array[key] = geos.map(geoHeader)
    geos.forEach((g) => pushGeoBuffers(g, buffers))
  }

  const headerBytes = new TextEncoder().encode(JSON.stringify(header))
  const bodyBytes = buffers.reduce((sum, b) => sum + b.byteLength, 0)
  const out = new ArrayBuffer(4 + headerBytes.byteLength + bodyBytes)
  new DataView(out).setUint32(0, headerBytes.byteLength, true)
  new Uint8Array(out, 4, headerBytes.byteLength).set(headerBytes)

  let offset = 4 + headerBytes.byteLength
  for (const b of buffers) {
    new Uint8Array(out, offset, b.byteLength).set(new Uint8Array(b.buffer, b.byteOffset, b.byteLength))
    offset += b.byteLength
  }
  return out
}

export const decodeWorkerOutput = (buffer: ArrayBuffer): WorkerOutput => {
  const view = new DataView(buffer)
  const headerLen = view.getUint32(0, true)
  const header = JSON.parse(new TextDecoder().decode(new Uint8Array(buffer, 4, headerLen))) as CacheHeader
  if (header.v !== GEOMETRY_CACHE_VERSION) {
    throw new Error(`Geometry cache version mismatch: ${header.v} !== ${GEOMETRY_CACHE_VERSION}`)
  }

  let offset = 4 + headerLen
  const readF32 = (len: number): Float32Array => {
    const arr = new Float32Array(len)
    new Uint8Array(arr.buffer).set(new Uint8Array(buffer, offset, len * 4))
    offset += len * 4
    return arr
  }
  const readU32 = (len: number): Uint32Array => {
    const arr = new Uint32Array(len)
    new Uint8Array(arr.buffer).set(new Uint8Array(buffer, offset, len * 4))
    offset += len * 4
    return arr
  }
  const readGeo = (h: GeoHeader): SerializedGeo => ({
    position: readF32(h.p),
    normal: readF32(h.n),
    uv: h.u !== null ? readF32(h.u) : null,
    index: h.i !== null ? readU32(h.i) : null,
  })

  // Build the output in the SAME traversal order used by encode.
  const result = {} as Record<string, SerializedGeo | SerializedGeo[]>
  for (const key of SINGLE_FIELDS) result[key] = readGeo(header.single[key])
  for (const key of ARRAY_FIELDS) result[key] = header.array[key].map(readGeo)
  return result as unknown as WorkerOutput
}
